import { Component, inject, Resource } from '@angular/core';
import { commonImports, tablePropInt } from '../../shared/table-interface';
import { HttpClient, HttpContext } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom, forkJoin, last, map, of, switchMap, tap, expand, reduce, scan, EMPTY, combineLatest } from 'rxjs';
import { PatientDetailsKeyService } from '../../patient-sidedetails/patient-details-key.service';
import { ErrorService } from '../../shared/error.service';
import { UtilityService } from '../../shared/utility.service';
import { MatTableDataSource } from '@angular/material/table';
import { Bundle, BundleEntry, Task, TaskInput, ChargeItem, Invoice, ServiceRequest } from 'fhir/r4';
import { TaskDetailsComponent } from '../task-details/task-details.component';
import { baseStatusStyles } from '../../shared/statusUIIcons';
import { LinkInReferencesService } from '../../shared/link-in-references.service';
import { AuthService } from '../../shared/auth/auth.service';
import { fetchFromReferencePipe } from "../../shared/Fetch.pipe";
import { AdmittedPatientsComponent } from '../../admitted-patients/admitted-patients.component';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../../shared/state.service';
import { ResourceStatusChangesService } from '../../resource-status-changes/resource-status-changes.service';
import { LabBillingPlaceholderService } from '../../shared/services/lab-billing-placeholder.service';
import { ServiceRequestBillingPlaceholderService } from '../../shared/services/service-request-billing-placeholder.service';
import { inferServiceRequestType } from '../../shared/service-request-type.util';
import { LoadingUIEnabled } from '../../loading.interceptor';
import { isHospitalAdmin, normalizeRole } from '../../shared/system-roles';

import { PatientNamePipe } from '../../shared/pipes/patient-name.pipe';
import { PatientAgePipe } from '../../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../../shared/pipes/patient-contacts.pipe';
import { DatePipe, TitleCasePipe, CommonModule } from '@angular/common';
import { EmptyStateComponent } from '../../shared/empty-state/empty-state.component';
import { PatientAddressPipe } from '../../shared/pipes/patient-address.pipe';
import { TestingTasksComponent } from '../../testing-tasks/testing-tasks.component';
import { CarePlanFormComponent } from '../../admission/care-plan-form/care-plan-form.component';
import { CarePlanDialogService } from '../../shared/care-plan-dialog.service';
import { ChipsDirective } from '../../chips.directive';
import { ReferenceDisplayDirective } from '../../shared/reference-display.directive';
import { EmphasisDirective } from '../../shared/directives/emphasis.directive';
import { FulfillTaskComponent } from '../fulfill-task/fulfill-task.component';
import { TaskPreviewDialogComponent } from '../../testing-tasks/task-preview-dialog/task-preview-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { ViewChild, AfterViewInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-tasks-table',
  standalone: true,
  imports: [...commonImports, fetchFromReferencePipe, EmphasisDirective,
    ChipsDirective, ReferenceDisplayDirective, CommonModule, MatTooltipModule,
    MatTabsModule, MatPaginatorModule,
    ReactiveFormsModule, PatientNamePipe, PatientAgePipe, PatientContactsPipe, DatePipe, TitleCasePipe, EmptyStateComponent],
  templateUrl: './tasks-table.component.html',
  providers: [PatientNamePipe, PatientAgePipe, PatientContactsPipe, PatientAddressPipe, DatePipe, TitleCasePipe,

  ],
  styleUrls: ['./tasks-table.component.scss']
})
export class TasksTableComponent extends AdmittedPatientsComponent {

  // Access parent services through local references
  private localStateService = this.stateService
  private localHttp = this.http
  private localBackendUrl = this.backendUrl
  private localErrorService = this.errorService;
  private snackBar = inject(MatSnackBar);
  
  private resolveBillingService = inject(LabBillingPlaceholderService);
  private serviceRequestPaymentResolver = inject(ServiceRequestBillingPlaceholderService);
  private statusChanges = inject(ResourceStatusChangesService);

  // Track current patient ID (will be set in ngOnInit)
  private currentPatientId: string | null = null;
  private loggedInPractitionerRef: string | null = null;

  sortedWithGroup: { [key: string]: any[] } = {};
  sortedWithGroupKeys: string[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Table columns configuration
  override displayedColumns: string[] = ['taskName', 'patient', 'startDate', 'status', 'assignedTo', 'actions'];

  /**
   * DATA PROCESSING FLOW - Overview of AdmittedPatientsComponent.ngOnInit() processes
   * ====================================================================================
   * 
   * STEP 1: INITIALIZE DATA SOURCE SUBSCRIPTION
   * --------------------------------------------
   * Method: this.inititateDataSourceSubscription(this.stateService.orgWideResources.encounters)
   * Purpose: Subscribe to the encounters observable from StateService and populate tableDataSource
   * Process: Maps each entry to its actualResource and assigns to tableDataSource.data
   * 
   * 
   * STEP 2: APPLY FILTERS TO DATA
   * ------------------------------
   * Method: this.applyFilter(filterKey, filterCallback)
   * 
   * Filter 1 - Status Filter:
   *   Key: 'status'
   *   Callback: (row) => row['status'] === 'in-progress'
   *   Purpose: Only show encounters with status 'in-progress'
   * 
   * Filter 2 - Hospitalization Filter:
   *   Key: 'hospitalization'
   *   Callback: (row) => row['hospitalization'] !== undefined && row['hospitalization'].preAdmissionIdentifier?.value
   *   Purpose: Only show encounters with valid hospitalization and preAdmissionIdentifier
   * 
   * Filter 3 - Class Filter:
   *   Key: 'class'
   *   Callback: (row) => row['class'] && row['class'].code === 'IMP'
   *   Purpose: Only show inpatient encounters (class code 'IMP')
   * 
   * 
   * STEP 3: SORT DATA
   * -----------------
   * Method: this.sortData(['hospitalization.preAdmissionIdentifier.period.start'], false)
   * Purpose: Sort encounters by admission date in descending order (newest first)
   * Sort Key: 'hospitalization.preAdmissionIdentifier.period.start'
   * Direction: false (descending)
   * 
   * 
   * STEP column: COMBINE REFERENCED RESOURCES
   * -------------------------------------
   * Method: this.combineReferencedResources([{ resourceType: 'Patient', attachKey: 'patientDetails' }], 'subject')
   * Purpose: Fetch and attach Patient resources referenced in encounter.subject
   * Process:
   *   - Extracts all unique Patient references from encounter.subject
   *   - Checks StateService cache first for Patient resources
   *   - Fetches missing Patients from backend if not in cache
   *   - Attaches Patient resource to each row as 'patientDetails' property
   * 
   * 
   * STEP 5: TRANSFORM DATA - EXTRACT WARD/ROOM/BED INFORMATION
   * -----------------------------------------------------------
   * Method: this.transformData((row) => { ... })
   * Purpose: Extract and normalize location data (ward, room, bed) and admission date
   * Transformations:
   *   
   *   a) Extract Admission Date:
   *      Source: row.hospitalization?.preAdmissionIdentifier?.period?.start
   *      Target: row.admittedDate
   *   
   *   b) Extract Location Information from encounter.location array:
   *      - Gets the last location entry (most recent)
   *      - Extracts admission date from location.period?.start
   *      - Finds all locations matching the admission date
   *      - Separates locations into ward/room/bed using physicalType codes:
   *        * 'wa' code or 'ward' in display → Ward
   *        * 'ro' code or 'room' in display → Room
   *        * 'bd' code or 'bed' in display → Bed
   *      - If no physicalType, extracts from location.display directly
   *      - Takes the last (most recent) ward, room, and bed
   *   
   *   c) Returns Enhanced Row:
   *      {
   *        ...row,
   *        admittedDate: ISO date string,
   *        currentWard: string or null,
   *        currentRoom: string or null,
   *        currentBed: string or null
   *      }
   * 
   * 
   * STEP 6: ORGANIZE SEARCH FILTERS FOR UI
   * ---------------------------------------
   * Method: this.organizingSearchFilters(new Map([['ward', ['ICU', 'General', 'Pediatrics', 'Maternity']]]))
   * Purpose: Setup filter form controls for table header search UI
   * Process:
   *   - Creates FormGroup for each filter category
   *   - Adds FormControl for each filter value (initialized as false checkboxes)
   *   - Stores in tableHeaderFiltersFormControlObject
   * 
   * 
   * FINAL RESULT:
   * =============
   * tableDataSource.data contains:
   *   - Filtered encounters (in-progress, hospitalized, inpatient only)
   *   - Sorted by admission date (newest first)
   *   - Enhanced with patientDetails from referenced Patient resources
   *   - Transformed with extracted admittedDate, currentWard, currentRoom, currentBed
   *   - Ready for table display with filter UI controls
   */

  // Inject the CarePlanDialogService
  private carePlanDialogService = inject(CarePlanDialogService);

  currentFilter: string = 'all tasks'
  additionalFilters: string[] = ['all tasks'];

  updateAdditionalFilters(): void {
    const filters = ['all tasks'];
    if (this.canShowMyTasksFilter()) {
      filters.push('my tasks');
    }
    filters.push('overdue tasks', 'today tasks', 'completed tasks');
    this.additionalFilters = filters;
  }

  applyAdditionalFilter(additionalFilter: string) {
    this.currentFilter = additionalFilter;
    this.applyTaskFiltersAndTransforms();
  }

  /**
   * Open care plan form dialog with admissionSubmitted set to true
   * This allows task creation within the care plan form
   */
  addTask() {
    // Fetch or create care plan first
    if (this.carePlanId) {
      // Care plan exists, open dialog with existing ID
      this.carePlanDialogService.openCarePlanDialog(this.carePlanId, true);
    } else {
      // No care plan yet, create one first
      const dialogRef = this.carePlanDialogService.openCarePlanDialog(undefined, true);

      // Listen for care plan creation
      dialogRef.afterClosed().subscribe((result) => {
        if (result?.carePlanId) {
          this.carePlanId = result.carePlanId;
          console.log('Care plan created with ID:', this.carePlanId);
        }
      });
    }
  }
  carePlanId?: string | null = null;



  override ngOnInit(): void {
    const currentUser = this.auth.currentUserValue;
    this.loggedInPractitionerRef = currentUser?.fhirResourceId
      ? `Practitioner/${currentUser.fhirResourceId}`
      : null;

    this.stateService.currentPatientId$.pipe(switchMap((patientId: any) => {
      if (patientId) {
        this.displayedColumns = this.displayedColumns.filter(col => col !== 'patient');
        return this.http.get(`${this.backendUrl}/CarePlan?status=active&patient=${this.stateService.currentPatientId$?.getValue()}`).pipe(
          map((data: any) => {

            if (data.entry && data.entry.length > 0
              && data.entry[0].resource.resourceType === 'CarePlan'
            ) {
              this.isCarePlanForThisPatient = true;
              this.carePlanId = data.entry[0].resource.id;
// $#$              // /* alert(this.carePlanId); */
            }
          }
          ))
      } else {
        return of(null);

      }
    })).subscribe();
    this.computePermissions('task');
    this.updateAdditionalFilters();

    // Use the BehaviorSubject currentPatientId$ with getValue() for synchronous access
    // This BehaviorSubject combines patient ID from multiple sources (encounter, patient resource, resolver)
    combineLatest([this.localStateService.currentPatientId$, this.auth.user]).subscribe({
      next: ([e, user]) => {
        this.currentPatientId = e
        this.currentLoggedInPractitionerRole = user?.role
        const role = user?.role;
        if (isHospitalAdmin(role) || normalizeRole(role) === 'superadmin') {
          this.tabs = ['All Active Tasks', 'All Tasks'];
          if (this.activeTabIndex >= this.tabs.length) {
            this.activeTabIndex = 0;
          }
        } else {
          this.tabs = ['My Active Tasks', 'Role Tasks', 'Requested by Me', 'All My Tasks'];
        }
        this.loadTasksForActiveTab();
      }
    })
  }
  currentLoggedInPractitionerRole?: string


  activeTabIndex = 0;
  tabs = ['My Active Tasks', 'Role Tasks', 'Requested by Me', 'All My Tasks'];

  onTabChange(event: MatTabChangeEvent) {
    this.activeTabIndex = event.index;
    this.loadTasksForActiveTab();
  }

  onCustomTabChange(index: number) {
    this.activeTabIndex = index;
    this.loadTasksForActiveTab();
  }

  private fetchAllPages(initialUrl: string, inBackground: boolean = false): Observable<Bundle> {
// $#$    /* alert(initialUrl) */
    const requestOptions = inBackground 
      ? { context: new HttpContext().set(LoadingUIEnabled, false) }
      : {};
    const resourceType = this.extractResourceTypeFromUrl(initialUrl);

    return this.localHttp.get<Bundle>(initialUrl, requestOptions).pipe(
      expand((bundle: Bundle) => {
        const nextLink = bundle.link?.find(l => l.relation === 'next')?.url;
        const normalizedNextLink = this.normalizeNextPageUrl(nextLink, resourceType);
        return normalizedNextLink ? this.localHttp.get<Bundle>(normalizedNextLink, requestOptions) : EMPTY;
      }),
      (inBackground ? scan : reduce)((acc: Bundle, curr: Bundle) => {
        if (!acc.entry) acc.entry = [];
        if (curr.entry) acc.entry.push(...curr.entry);
        return acc;
      }, { resourceType: 'Bundle', type: 'searchset', entry: [] } as Bundle)
    );
  }

  private normalizeNextPageUrl(nextLink: string | undefined, resourceType: string): string {
    const raw = (nextLink || '').trim();
    if (!raw) {
      return '';
    }

    if (raw.startsWith('/')) {
      return `${this.localBackendUrl.replace(/\/$/, '')}${raw}`;
    }

    try {
      const parsed = new URL(raw);
      let subPath = parsed.pathname;

      // Remove any prefix from the path that matches our backend's known prefixes,
      // because we will be appending this subPath directly to this.localBackendUrl.
      if (subPath.startsWith('/authBackend/fhir/')) {
        subPath = subPath.substring(17); // Keep the slash: '/PractitionerRole'
      } else if (subPath.startsWith('/authBackend/')) {
        subPath = subPath.substring(12);
      } else if (subPath.startsWith('/fhir/')) {
        subPath = subPath.substring(5); // Keep the slash: '/PractitionerRole'
      }

      // Ensure subPath starts with a slash
      if (subPath && !subPath.startsWith('/')) {
        subPath = '/' + subPath;
      }

      return `${this.localBackendUrl.replace(/\/$/, '')}${subPath}${parsed.search}`;
    } catch {
      // Keep original raw URL if parsing fails or if it's already normalized
    }

    return raw;
  }

  private extractResourceTypeFromUrl(url: string): string {
    const path = (url || '').split('?')[0];
    const segments = path.split('/').filter(Boolean);
    return segments[segments.length - 1] || '';
  }

  private getRolesQueryForCurrentUser(): Observable<string> {
    const currentUser = this.auth.user.subscribe
    // const userRoles = currentUser?.roles?.map(r => r.toLowerCase()) || [];
    const userRole = this.currentLoggedInPractitionerRole
    // if (userRole.length === 0) return of('');

    return this.fetchAllPages(`${this.localBackendUrl}/PractitionerRole?role:text=${userRole}`).pipe(
      map(bundle => {
        const matchingIds: string[] = [];
        bundle.entry?.forEach((entry: any) => {
          const resource = entry.resource;
          const display = (resource.code?.[0]?.coding?.[0]?.display || resource.code?.[0]?.text || '').toLowerCase();

          if (userRole?.toLowerCase().trim() === display) {
            matchingIds.push(`PractitionerRole/${resource.id}`);
          }
        });
        return matchingIds.join(',');
      })
    );
  }

  loadTasksForActiveTab() {
    if (!this.loggedInPractitionerRef) return;

    const practitionerId = this.loggedInPractitionerRef.split('/')[1];
    const patientParam = this.currentPatientId ? `&patient=Patient/${this.currentPatientId}` : '';

    this.getRolesQueryForCurrentUser().pipe(
      switchMap(rolesQuery => {
        let queries: Observable<any>[] = [];
        let isAllTab = false;

        const role = this.currentLoggedInPractitionerRole;
        const isAdmin = isHospitalAdmin(role) || normalizeRole(role) === 'superadmin';

        if (isAdmin) {
          switch (this.activeTabIndex) {
            case 0: // All Active Tasks
              queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?status:not=completed${patientParam}`, true));
              break;
            case 1: // All Tasks
              isAllTab = true;
              const adminAllUrl = this.currentPatientId 
                ? `${this.localBackendUrl}/Task?patient=Patient/${this.currentPatientId}` 
                : `${this.localBackendUrl}/Task?_count=1000`;
              queries.push(this.fetchAllPages(adminAllUrl, true));
              break;
          }
        } else {
          switch (this.activeTabIndex) {
            case 0: // My Active Tasks
              queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?owner=Practitioner/${practitionerId}&status:not=completed${patientParam}`));
              break;
            case 1: // Role Tasks
              if (rolesQuery) {
                queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?owner=${rolesQuery}&status:not=completed${patientParam}`));
              } else {
                queries.push(of({ entry: [] }));
              }
              break;
            case 2: // Requested by Me
              queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?requester=Practitioner/${practitionerId}&status:not=completed${patientParam}`));
              break;
            case 3: // All My Tasks (no status restriction)
              isAllTab = true;
              queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?owner=Practitioner/${practitionerId}${patientParam}`));
              if (rolesQuery) {
                queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?owner=${rolesQuery}${patientParam}`));
              }
              queries.push(this.fetchAllPages(`${this.localBackendUrl}/Task?requester=Practitioner/${practitionerId}${patientParam}`));
              break;
          }
        }

        return queries.length > 0 ? forkJoin(queries) : of([]);
      })
    ).subscribe(results => {
      if (results.length === 0) return;
      const allTasksMap = new Map<string, Task>();
      results.forEach((bundle: any) => {
        if (bundle.entry) {
          bundle.entry.forEach((entry: any) => {
            if (entry.resource && entry.resource.id) {
              allTasksMap.set(entry.resource.id, entry.resource);
            }
          });
        }
      });

      const tasksArray = Array.from(allTasksMap.values());
      this.initiateDataSourceSubscription(of(tasksArray));
      this.applyTaskFiltersAndTransforms();

      // Link paginator when data updates
      setTimeout(() => {
        if (this.paginator) {
          this.tableDataSource.paginator = this.paginator;
        }
      });
    });
  }

  /**
   * Load tasks for a specific patient from PatientResources
   */
  private loadPatientSpecificTasks(): void {
    let currentTasks;
    if (this.localStateService.currentPatientId$.getValue()) {
// $#$      // /* alert(this.currentPatientId); */
      currentTasks = this.localStateService.PatientResources.tasks.pipe(map((e: any) => {
        return e;
      }),
        // last(),
        switchMap((localTaskResources: {
          referenceId: string; actualResource: Task; savedStatus: 'saved' | 'unsaved';
        }[]) => {
          // console.log('Filtered patient-specific tasks bundle:', bundle);
          if (localTaskResources.length > 0) {
            return of(localTaskResources.map(e => e.actualResource)).pipe(map(tasks => {
              console.log('Using local patient-specific tasks:', tasks, 'tasks found');
              return tasks.filter((task: any) => task.for?.reference === `Patient/${this.currentPatientId}`)

            }));
          } else {
            return this.localHttp.get<Bundle>(`${this.localBackendUrl}/Task?_count=10000`).pipe(
              tap((bundle: Bundle) => {
                this.localStateService.processBundleTransaction(bundle);
                this.localStateService.processOrgWideBundleTransaction(bundle);

              }))

          }

        }),
      );
    } else {
      currentTasks = this.localStateService.orgWideResources.tasks.pipe(map((e: any) => {
        return e;
      }),
        switchMap((localTaskResources: {
          referenceId: string; actualResource: Task; savedStatus: 'saved' | 'unsaved';
        }[]) => {
          // console.log('Filtered patient-specific tasks bundle:', bundle);
          if (localTaskResources.length > 0) {
            return of(localTaskResources.map(e => e.actualResource));
          } else {
            return this.localHttp.get<Bundle>(`${this.localBackendUrl}/Task?_count=10000`).pipe(
              tap((bundle: Bundle) => {
                // this.localStateService.processBundleTransaction(bundle);
                this.localStateService.processOrgWideBundleTransaction(bundle);

              }),
            );
          }
        }),

      );
    }

    this.initiateDataSourceSubscription(currentTasks);
    this.applyTaskFiltersAndTransforms();

  }

  // if (!currentTasks || currentTasks.length === 0) {
  //   // Fetch patient-specific tasks from backend if not in PatientResources
  //   console.log('Patient tasks not found in PatientResources, fetching from backend...');
  //   this.localHttp.get<Bundle>(`${this.localBackendUrl}/Task?for=Patient/${this.currentPatientId}&_count=10000`).pipe(map((e: any) => {
  //     return { ...e, entry: e.entry?.filter((entry: BundleEntry<Task>) => entry.resource?.for?.reference === `Patient/${this.currentPatientId}`) }
  //   }))
  //     .subscribe({
  //       next: (bundle) => {
  //         console.log('Fetched ' + (bundle.entry?.length || 0) + ' tasks for patient ' + this.currentPatientId);

  //         // Check if there's a current patient ID from StateService
  //         const currentPatientId = this.localStateService.currentPatientId$.getValue();
// $#$  //         // /* alert(currentPatientId); */
  //         if (currentPatientId) {
  //           // If patient ID exists, use both methods
  //           console.log('Patient ID exists, using both processBundleTransaction and processOrgWideBundleTransaction');

  //         } else {
  //           // No patient ID, use only org-wide
  //           console.log('No patient ID, using only processOrgWideBundleTransaction');
  //           this.localStateService.processOrgWideBundleTransaction(bundle);
  //         }

  //         // Subscribe to PatientResources.tasks for patient-specific context
  //         if (this.localStateService.currentPatientId$.getValue()) {
  //           this.initiateDataSourceSubscription(this.localStateService.PatientResources.tasks);
  //         } else {
  //           this.initiateDataSourceSubscription(this.localStateService.orgWideResources.tasks);
  //         }
  //         this.applyTaskFiltersAndTransforms();
  //       },
  //       error: (err) => {
  //         this.localErrorService.openandCloseError('Failed to load patient tasks: ' + err);
  //       }
  //     });
  // } else {
  //   // Tasks already in PatientResources, use them directly
  //   console.log('Using existing patient tasks from PatientResources');
  //   this.initiateDataSourceSubscription();
  //   this.applyTaskFiltersAndTransforms();
  // }
  // }})

  /**
   * Load all organization-wide tasks from orgWideResources
   */
  private loadOrgWideTasks(): void {
    const currentTasks = this.localStateService.orgWideResources.tasks.getValue();

    if (!currentTasks || currentTasks.length === 0) {
      // Fetch org-wide tasks from backend if not in orgWideResources
      console.log('Org-wide tasks not found in orgWideResources, fetching from backend...');
      this.localHttp.get<Bundle>(`${this.localBackendUrl}/Task?_count=10000`)
        .subscribe({
          next: (bundle) => {
            // if resourcetype is Operation Outcome
            if (bundle.entry && bundle.entry.length > 0 && bundle.entry[0].resource?.resourceType === 'OperationOutcome') {
              throw new Error('OperationOutcome received instead of Task resources' + JSON.stringify(bundle.entry[0].resource));
            }

            // Check if there's a current patient ID from StateService
            const currentPatientId = this.localStateService.currentPatientId$.getValue();

            if (currentPatientId) {
              // If patient ID exists, use both methods
              console.log('Patient ID exists, using both processBundleTransaction and processOrgWideBundleTransaction');
              this.localStateService.processBundleTransaction(bundle);
              this.localStateService.processOrgWideBundleTransaction(bundle);
            } else {
              // No patient ID, use only org-wide
              console.log('No patient ID, using only processOrgWideBundleTransaction');
              this.localStateService.processOrgWideBundleTransaction(bundle);
            }

            // Now subscribe to the data source
            this.initiateDataSourceSubscription(this.localStateService.orgWideResources.tasks);
            this.applyTaskFiltersAndTransforms();
          },
          error: (err) => {
            this.localErrorService.openandCloseError('Failed to load org-wide tasks: ' + err);
          }
        });
    } else {
      // Tasks already in orgWideResources, use them directly
      console.log('Using existing org-wide tasks from orgWideResources');
      this.initiateDataSourceSubscription(this.localStateService.orgWideResources.tasks);
      this.applyTaskFiltersAndTransforms();
    }
  }

  private applyTaskFiltersAndTransforms(): void {
    this.applyFilter('task-view', (row) => this.matchesAdditionalTaskFilter(row));

    // STEP 2: Apply filters (customize for Task-specific filters)
    // Example: Filter by task status
    this.applyFilter('status', (row) => {
      // Show all tasks on 'All My Tasks' tab or when specifically viewing completed tasks
      if (this.activeTabIndex === 3 || this.currentFilter === 'completed tasks') {
        return true;
      }
      // Otherwise, hide completed and cancelled tasks
      return row['status'] !== 'completed' && row['status'] !== 'cancelled';
    });

    // STEP 3: Sort by restriction.period.start or authoredOn (descending - newest first)
    this.sortData(['restriction.period.start', 'authoredOn'], false);

    // STEP column: Combine referenced resources
    // Attach Patient details from task.for reference
    this.combineReferencedResources(
      [
        { resourceType: 'Patient', attachKey: 'patientDetails' }
      ],
      'for'
    );

    // STEP 5: Transform data to extract relevant task information
    this.transformData((row) => {
      console.log('Transforming task row:', row);
      const focusCategory = this.getTaskCategory(row);
      return {
        ...row,
        startDate: row.restriction?.period?.start || row.authoredOn,
        dueDate: row.restriction?.period?.end,
        taskName: row.description || row.code?.text || 'N/A',
        assignedTo: row.owner?.display || row.owner?.reference || ' N / A',
        overdue: row.restriction?.period?.end ? new Date() > new Date(row.restriction.period.end) : false,
        focusCategory
      };
    });

    // STEP 5.5: Filter service provider tasks based on category access
    const currentUser = this.auth.getCurrentUser();
    if (currentUser && currentUser.role.toLowerCase() === 'alliedhealthprofessional') {
      this.applyFilter('serviceprovider-tasks', (row) => {
        const category = row['focusCategory'] || this.getTaskCategory(row);
        return this.auth.canAccessServiceRequestCategory(category) || this.auth.canAccessObservationCategory(category);
      });
    }

    // STEP 6: Organize search filters for UI
    this.organizingSearchFilters(new Map<string, any[]>([
      ['status', ['draft', 'requested', 'received', 'accepted', 'in-progress', 'on-hold']],
      ['intent', ['order', 'plan', 'proposal']],
    ]));

    this.executeTransforms();

  }

  /**
   * Open fulfill task dialog
   */
  fulfillTask(task: Task) {
    const dialogRef = this.dialog.open(FulfillTaskComponent, {
      width: '96vw',
      maxWidth: '900px',
      maxHeight: '92vh',
      data: {
        task,
        purpose: 'fulfill',
        patientId: this.stateService.currentPatientId$.getValue() || undefined
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.applyTaskFiltersAndTransforms();
      }
    });
  }

  /**
   * Open complete task dialog
   */
  completeTask(task: Task) {
    const dialogRef = this.dialog.open(FulfillTaskComponent, {
      width: '96vw',
      maxWidth: '800px',
      maxHeight: '92vh',
      data: {
        task: task,
        purpose: 'complete',
        patientId: this.stateService.currentPatientId$.getValue()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Task completed:', result);
        this.applyTaskFiltersAndTransforms();
      }
    });
  }

  viewTaskDetails(task: Task): void {
    this.dialog.open(FulfillTaskComponent, {
      width: '96vw',
      maxWidth: '900px',
      maxHeight: '92vh',
      data: {
        task,
        purpose: 'review',
        patientId: this.stateService.currentPatientId$.getValue() || undefined
      }
    });
  }

  getTaskCategory(task: Task | any): string {
    const focusRef = task.focus?.reference || '';
    if (focusRef.startsWith('MedicationRequest/')) {
      return 'medication';
    }

    if (!focusRef.startsWith('ServiceRequest/')) {
      const text = `${task.description || ''} ${task.code?.text || ''} ${task.code?.coding?.[0]?.display || ''}`.toLowerCase();
      if (text.includes('medication') || text.includes('drug') || text.includes('prescrip')) return 'medication';
      if (text.includes('vital') || text.includes('bp') || text.includes('blood pressure') || text.includes('temp') || text.includes('pulse') || text.includes('observation')) return 'observation';
      if (text.includes('lab') || text.includes('blood test') || text.includes('urine') || text.includes('sputum') || text.includes('test')) return 'laboratory';
      if (text.includes('xray') || text.includes('x-ray') || text.includes('scan') || text.includes('mri') || text.includes('ultrasound') || text.includes('imaging')) return 'imaging';
      return 'unknown';
    }

    const srId = focusRef.split('/')[1] || focusRef;
    let serviceRequestResource: any = null;
    const cachedPatient = this.localStateService.PatientResources.serviceRequests.value?.find((r: any) =>
      r.actualResource?.id === srId || r.referenceId === focusRef
    );
    if (cachedPatient) {
      serviceRequestResource = cachedPatient.actualResource;
    } else {
      const cachedOrg = this.localStateService.orgWideResources.serviceRequests.value?.find((r: any) =>
        r.actualResource?.id === srId || r.referenceId === focusRef
      );
      if (cachedOrg) {
        serviceRequestResource = cachedOrg.actualResource;
      }
    }

    if (serviceRequestResource) {
      const categories = serviceRequestResource.category || [];
      const isLab = categories.some((c: any) =>
        (c.text && c.text.toLowerCase().includes('lab')) ||
        (c.coding && c.coding.some((co: any) => co.code === 'laboratory' || (co.display && co.display.toLowerCase().includes('lab'))))
      );
      if (isLab) return 'laboratory';

      const isImaging = categories.some((c: any) =>
        (c.text && c.text.toLowerCase().includes('imaging')) ||
        (c.coding && c.coding.some((co: any) => co.code === 'imaging' || (co.display && co.display.toLowerCase().includes('imaging'))))
      );
      if (isImaging) return 'imaging';

      const isObs = categories.some((c: any) =>
        (c.text && c.text.toLowerCase().includes('observation')) ||
        (c.coding && c.coding.some((co: any) => co.code === 'observation' || (co.display && co.display.toLowerCase().includes('observation'))))
      );
      if (isObs) return 'observation';
    }

    const textToCheck = `${task.description || ''} ${task.code?.text || ''} ${task.code?.coding?.[0]?.display || ''}`.toLowerCase();
    if (textToCheck.includes('medication') || textToCheck.includes('drug') || textToCheck.includes('prescrip')) return 'medication';
    if (textToCheck.includes('vital') || textToCheck.includes('bp') || textToCheck.includes('blood pressure') || textToCheck.includes('temp') || textToCheck.includes('pulse') || textToCheck.includes('observation')) return 'observation';
    if (textToCheck.includes('lab') || textToCheck.includes('blood test') || textToCheck.includes('urine') || textToCheck.includes('sputum') || textToCheck.includes('test')) return 'laboratory';
    if (textToCheck.includes('xray') || textToCheck.includes('x-ray') || textToCheck.includes('scan') || textToCheck.includes('mri') || textToCheck.includes('ultrasound') || textToCheck.includes('imaging')) return 'imaging';

    return 'unknown';
  }

  canFulfillTask(task: Task | any): boolean {
    return this.auth.can('task', 'fulfill', task['focusCategory']);
  }

  canCompleteTask(task: Task | any): boolean {
    return this.auth.can('task', 'complete', task['focusCategory']);
  }

  canViewTask(): boolean {
    return this.auth.can('task', 'viewAll') || this.auth.can('task', 'viewSelf');
  }

  canCancelTask(task: any): boolean {
    const currentUser = this.auth.getCurrentUser();
    if (!currentUser) return false;
    if (currentUser.isSuperAdmin || isHospitalAdmin(currentUser.role)) {
      return true;
    }
    if (!task.requester?.reference) {
      return true; // For backwards compatibility
    }
    return !!this.loggedInPractitionerRef && task.requester.reference === this.loggedInPractitionerRef;
  }

  canCancelTaskGroup(groupId: string): boolean {
    const tasks = this.sortedWithGroup[groupId] || [];
    if (tasks.length === 0) return false;
    return tasks.every(t => this.canCancelTask(t));
  }

  canShowMyTasksFilter(): boolean {
    return !!this.loggedInPractitionerRef;
  }

  private isTaskOwnedByCurrentPractitioner(task: Task | any): boolean {
    const ownerRef = (task?.owner?.reference || '').toString().trim();
    return !!this.loggedInPractitionerRef && ownerRef === this.loggedInPractitionerRef;
  }

  private matchesAdditionalTaskFilter(row: Task | any): boolean {
    if (this.currentFilter === 'my tasks') {
      return this.isTaskOwnedByCurrentPractitioner(row);
    }

    if (this.currentFilter === 'overdue tasks') {
      return !!row.restriction?.period?.end && new Date() > new Date(row.restriction.period.end);
    }

    if (this.currentFilter === 'today tasks') {
      const today = new Date();
      return !!row.restriction?.period?.start
        && new Date(row.restriction.period.start).toDateString() === today.toDateString();
    }

    if (this.currentFilter === 'completed tasks') {
      return row.status === 'completed';
    }

    return true;
  }

  groupByTaskGroup(tasks: any[]): void {
    const sorted: { [key: string]: any[] } = {};

    tasks.forEach((task) => {
      const groupValue = task['groupIdentifier']?.value;
      if (groupValue) {
        if (!sorted[groupValue]) {
          sorted[groupValue] = [];
        }
        sorted[groupValue].push(task);
      } else {
        const fallbackKey = 'ungrouped-' + (task.id || Math.random().toString(36).substring(2, 9));
        sorted[fallbackKey] = [task];
      }
    });

    this.sortedWithGroup = sorted;
    this.sortedWithGroupKeys = Object.keys(sorted);
  }

  override executeTransforms(): void {
    super.executeTransforms();

    const tasks = [...this.tableDataSource.data];

    // Re-apply pure date sort here because super's initiateDataSourceSubscription
    // replaces the data array *after* ngOnInit's applyTaskFiltersAndTransforms runs.
    tasks.sort((a, b) => {
      const dateA = new Date(a['restriction']?.period?.start || a['authoredOn'] || 0).getTime();
      const dateB = new Date(b['restriction']?.period?.start || b['authoredOn'] || 0).getTime();
      return dateB - dateA;
    });

    this.groupByTaskGroup(tasks);

    // Pre-calculate properties to prevent template method-call spam which causes hanging
    tasks.forEach(task => {
      const groupVal = task['groupIdentifier']?.value;
      if (groupVal) {
        task['_groupCount'] = (this.sortedWithGroup[groupVal] || []).length;
        task['_canCancelGroup'] = this.canCancelTaskGroup(groupVal);
        task['_groupValueString'] = String(groupVal);
      }
      task['_canCancelTask'] = this.canCancelTask(task);
      task['_canFulfillTask'] = this.canFulfillTask(task);
      task['_canCompleteTask'] = this.canCompleteTask(task);
      task['_canViewTask'] = this.canViewTask();
    });

    this.tableDataSource.data = tasks;
  }

  // Helper method no longer used in template, kept for internal logic
  getGroupTaskCount(groupId: string): number {
    return (this.sortedWithGroup[groupId] || []).length;
  }

  private async buildBillingSnapshot(
      sr: ServiceRequest,
      patientIdOverride?: string | null
  ): Promise<{ patientId: string; chargeItem: ChargeItem | null; invoices: Invoice[]; hasBalancedInvoice: boolean }> {
      const resolvedPatientId = patientIdOverride
          || sr.subject?.reference?.split('/').pop()
          || this.currentPatientId
          || '';

      if (!resolvedPatientId) {
          return { patientId: '', chargeItem: null, invoices: [], hasBalancedInvoice: false };
      }

      const type = inferServiceRequestType(sr);
      if (type === 'laboratory') {
          const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForLab(sr, resolvedPatientId));
          return {
              patientId: resolvedPatientId,
              chargeItem: billing?.chargeItem ?? null,
              invoices: billing?.linkedInvoices ?? [],
              hasBalancedInvoice: !!billing?.hasBalancedInvoice
          };
      }
      if (type === 'imaging') {
          const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForImagingStudy(sr, resolvedPatientId));
          return {
              patientId: resolvedPatientId,
              chargeItem: billing?.chargeItem ?? null,
              invoices: billing?.linkedInvoices ?? [],
              hasBalancedInvoice: !!billing?.hasBalancedInvoice
          };
      }
      if (type === 'procedure' || type === 'surgery') {
          const billing = await firstValueFrom(this.resolveBillingService.resolveBillingAndPlaceholderForProcedure(sr, resolvedPatientId));
          return {
              patientId: resolvedPatientId,
              chargeItem: billing?.chargeItem ?? null,
              invoices: billing?.linkedInvoices ?? [],
              hasBalancedInvoice: !!billing?.hasBalancedInvoice
          };
      }

      const fallback = await firstValueFrom(
          this.serviceRequestPaymentResolver.resolvePaymentForServiceRequest(sr as ServiceRequest, resolvedPatientId)
      );
      return {
          patientId: resolvedPatientId,
          chargeItem: (fallback?.chargeItems || [])[0] ?? null,
          invoices: fallback?.invoices ?? [],
          hasBalancedInvoice: !!fallback?.hasBalancedInvoice
      };
  }

  private async resolveBillingSnapshotForRevocation(
      sr: ServiceRequest,
      patientId: string
  ): Promise<{ hasBalancedInvoice: boolean; invoices: Invoice[] }> {
      const snapshot = await this.buildBillingSnapshot(sr, patientId);
      return { hasBalancedInvoice: snapshot.hasBalancedInvoice, invoices: snapshot.invoices };
  }

  private async revokeServiceRequestAndInvoices(srId: string, patientId: string): Promise<void> {
      if (!srId) return;

      const sr = await firstValueFrom(this.localHttp.get<ServiceRequest>(`${this.localBackendUrl}/ServiceRequest/${srId}`));
      if (!sr) return;

      const status = (sr?.status ?? '').toString().toLowerCase();
      if (status === 'revoked') {
          return;
      }

      let paymentResult: { invoices?: Invoice[]; hasBalancedInvoice?: boolean } | null = null;
      try {
          paymentResult = await this.resolveBillingSnapshotForRevocation(sr, patientId);
      } catch (error) {
          console.error('Failed to resolve billing for ServiceRequest', srId, error);
          return;
      }

      const currentPatientId = this.localStateService.currentPatientId$.getValue() || '';
      const persistMode: 'auto' | 'patient' | 'org' = currentPatientId && currentPatientId === patientId
          ? 'patient'
          : 'auto';

      try {
          await firstValueFrom(this.statusChanges.changeResourceStatus('ServiceRequest', srId, 'revoked', persistMode));
      } catch (error) {
          console.error('Failed to revoke ServiceRequest', srId, error);
          return;
      }

      if (paymentResult?.hasBalancedInvoice) {
          return;
      }

      const fallbackInvoices: Invoice[] = Array.isArray((sr as any)?.relatedInvoices)
          ? (((sr as any).relatedInvoices as any[]).filter(Boolean) as Invoice[])
          : [];
      const invoices = (paymentResult?.invoices?.length ? paymentResult.invoices : fallbackInvoices) ?? [];

      const idsToCancel = Array.from(new Set(
          (invoices || [])
              .filter((inv: any) => {
                  const st = (inv?.status ?? '').toString().toLowerCase();
                  return st && st !== 'balanced' && st !== 'cancelled' && st !== 'entered-in-error';
              })
              .map((inv: any) => (inv?.id ?? '').toString().trim())
              .filter(Boolean)
      ));

      if (!idsToCancel.length) {
          return;
      }

      await Promise.all(idsToCancel.map(async (invoiceId) => {
          try {
              await firstValueFrom(this.statusChanges.changeResourceStatus('Invoice', invoiceId, 'cancelled', persistMode));
          } catch (error) {
              console.error('Failed to cancel Invoice', invoiceId, error);
          }
      }));
  }

  cancelTasksAndFocus(tasks: any[]): Observable<any> {
    if (!tasks || tasks.length === 0) return of(null);

    const updates: Observable<any>[] = [];

    // 1. Create a bundle for the Task updates (since we have the full Task resources)
    const taskEntries = tasks.map((task: any) => {
      const cleanTask = { ...task };
      const keysToRemove = [
        'patientDetails', 'startDate', 'dueDate', 'taskName', 
        'assignedTo', 'overdue', 'focusCategory'
      ];
      
      keysToRemove.forEach(key => delete cleanTask[key]);
      Object.keys(cleanTask).forEach(key => {
        if (key.startsWith('_')) {
          delete cleanTask[key];
        }
      });

      const updatedTask = {
        ...cleanTask,
        status: 'cancelled'
      };
      return {
        request: {
          method: 'PUT',
          url: `Task/${task.id}`
        },
        resource: updatedTask
      };
    });

    const taskBundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: taskEntries
    };

    updates.push(
      this.localHttp.post(`${this.localBackendUrl}`, taskBundle).pipe(
        tap((res: any) => {
          this.localStateService.processBundleTransaction(res);
          this.localStateService.processOrgWideBundleTransaction(res);
        })
      )
    );

    // 2. For each task, check if there's a focus ServiceRequest or MedicationRequest, and PATCH it
    const updatedFocusRefs = new Set<string>();
    tasks.forEach((task) => {
      const focusRef = task.focus?.reference || '';
      if (!focusRef || updatedFocusRefs.has(focusRef)) return;
      updatedFocusRefs.add(focusRef);

      const patientRef = task.for?.reference || '';
      const patientId = patientRef.split('/').pop() || '';

      if (focusRef.startsWith('ServiceRequest/')) {
        const srvId = focusRef.split('/')[1];
        updates.push(
          of(null).pipe(
            switchMap(() => this.revokeServiceRequestAndInvoices(srvId, patientId))
          )
        );
      } else if (focusRef.startsWith('MedicationRequest/')) {
        const medId = focusRef.split('/')[1];
        
        const currentPatientId = this.localStateService.currentPatientId$.getValue() || '';
        const persistMode: 'auto' | 'patient' | 'org' = currentPatientId && currentPatientId === patientId
            ? 'patient'
            : 'auto';
            
        updates.push(
          of(null).pipe(
            switchMap(() => this.statusChanges.changeResourceStatus('MedicationRequest', medId, 'cancelled', persistMode))
          )
        );
      }
    });

    return forkJoin(updates);
  }

  cancelTaskGroup(groupId: string): void {
    const tasksInGroup = this.sortedWithGroup[groupId] || [];
    if (tasksInGroup.length === 0) return;

    const confirmCancel = confirm(`Are you sure you want to cancel all ${tasksInGroup.length} tasks in this group?`);
    if (!confirmCancel) return;

    this.cancelTasksAndFocus(tasksInGroup).subscribe({
      next: () => {
        console.log(`Cancelled task group ${groupId} successfully`);
        tasksInGroup.forEach(t => t.status = 'cancelled');
        this.snackBar.open('Task group cancelled successfully.', 'Close', { duration: 3000, panelClass: 'success-snackbar' });
        this.applyTaskFiltersAndTransforms();
      },
      error: (err) => {
        console.error('Failed to cancel task group:', err);
        this.localErrorService.openandCloseError('Failed to cancel task group. Please try again.');
      }
    });
  }

  private cloneResource<T>(resource: T): T {
    return resource ? JSON.parse(JSON.stringify(resource)) : resource;
  }

  private sanitizeTaskForSubmission(task: any): any {
    const cleanTask = this.cloneResource(task);
    [
      'patientDetails',
      'startDate',
      'dueDate',
      'taskName',
      'assignedTo',
      'overdue',
      'focusCategory'
    ].forEach(key => delete cleanTask[key]);

    Object.keys(cleanTask).forEach(key => {
      if (key.startsWith('_')) {
        delete cleanTask[key];
      }
    });

    return cleanTask;
  }

  manageTaskGroup(groupId: string): void {
    const tasksInGroup = this.sortedWithGroup[groupId] || [];
    if (tasksInGroup.length === 0) return;

    const taskGroups = new Map<string, any[]>();
    taskGroups.set(groupId, tasksInGroup.map(task => this.sanitizeTaskForSubmission(task)));

    const patientId = tasksInGroup[0].for?.reference?.split('/')?.[1] || this.currentPatientId || 'Unknown';

    const dialogRef = this.dialog.open(TaskPreviewDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      maxHeight: '90vh',
      data: {
        allTasks: [...tasksInGroup],
        taskGroups: taskGroups,
        patientId: patientId,
        isExisting: true
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.confirmed) {
        const originalTasksMap = new Map<string, any>(tasksInGroup.map(t => [t.id, this.sanitizeTaskForSubmission(t)]));
        const updatedTasks = (result.tasks || []).map((task: any) => this.sanitizeTaskForSubmission(task));

        const tasksToUpdate: any[] = [];
        const tasksToCancel: any[] = [];

        updatedTasks.forEach((updated: any) => {
          const original = originalTasksMap.get(updated.id);
          if (original) {
            if (updated.status === 'cancelled' && original.status !== 'cancelled') {
              tasksToCancel.push(updated);
            } else if (JSON.stringify(updated) !== JSON.stringify(original)) {
              tasksToUpdate.push(updated);
            }
          }
        });

        const updates: Observable<any>[] = [];
        if (tasksToCancel.length > 0) {
          updates.push(this.cancelTasksAndFocus(tasksToCancel));
        }

        if (tasksToUpdate.length > 0) {
          const updateEntries = tasksToUpdate.map((task: any) => ({
            request: {
              method: 'PUT',
              url: `Task/${task.id}`
            },
            resource: task
          }));

          const updateBundle = {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: updateEntries
          };

          updates.push(
            this.localHttp.post(`${this.localBackendUrl}`, updateBundle).pipe(
              tap((res: any) => {
                this.localStateService.processBundleTransaction(res);
                this.localStateService.processOrgWideBundleTransaction(res);
              })
            )
          );
        }

        if (updates.length > 0) {
          forkJoin(updates).subscribe({
            next: () => {
              console.log('✅ Task group updated successfully');
              tasksToCancel.forEach(t => {
                const orig = tasksInGroup.find(task => task.id === t.id);
                if (orig) orig.status = 'cancelled';
              });
              tasksToUpdate.forEach(t => {
                const orig = tasksInGroup.find(task => task.id === t.id);
                if (orig) Object.assign(orig, t);
              });
              this.snackBar.open('Task group updated successfully.', 'Close', { duration: 3000, panelClass: 'success-snackbar' });
              this.applyTaskFiltersAndTransforms();
            },
            error: (err) => {
              console.error('Failed to update task group:', err);
              this.localErrorService.openandCloseError('Failed to save task group changes. Please try again.');
            }
          });
        }
      }
    });
  }

}
